import { useEffect, useState } from 'react';
import {
  Download,
  FileImage,
  Globe,
  Info,
  Plus,
  Presentation,
  Printer,
  Scissors,
  Sliders,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui';
import {
  downloadUrls,
  type CustomMode,
  type CustomType,
  type CuttingOrientation,
  type DownloadPreset,
} from '@/lib/api';
import { t } from '@/lib/i18n';
import {
  useDeletePreset,
  useDownloadFormats,
  useDownloadPresets,
  useSavePreset,
} from '@/lib/queries';
import type { Id } from '@shared/types';
import { formatLabel } from '@/lib/server-text';

/**
 * The download panel from the old Pimcore asset view, rebuilt.
 *
 * Three tiers, matching what people actually used there:
 *   1. Original file — always available, whatever the type.
 *   2. Named formats (Web / Print / Office) — one click, the common cases.
 *   3. Cutting — the die-cut mockup export, for packshots carrying a Photoshop
 *      clipping path. Image-only and server-capability-gated, so it appears
 *      only when the asset and the box can both actually produce one.
 *   4. Custom — format, scaling mode, dimensions, quality and DPI, with the
 *      settings saveable as a named preset so a repeated spec becomes a
 *      dropdown entry instead of five fields retyped each time.
 *
 * Everything below the original is image-only: the server can only rasterise
 * images, so for a PDF or video we show the original alone rather than buttons
 * that would fail.
 */
/**
 * Icon and colour per download choice, so the four rows are told apart at a
 * glance instead of by reading three similar lines of grey text.
 *
 * Keyed by the server's format id (see server/download-formats.ts). An id with
 * no entry here falls back to ORIGINAL_STYLE's neutral treatment rather than
 * rendering blank, so adding a format server-side degrades gracefully.
 *
 * Colours are the hue's 50/600 in light and 500/950-ish in dark — the same
 * wash-tint-plus-saturated-glyph pattern the brand ramp uses for selected rows,
 * which keeps the glyph above 4.5:1 on its own tile in both themes.
 */
interface RowStyle {
  Icon: typeof Download;
  tile: string;
  glyph: string;
}

const ORIGINAL_STYLE: RowStyle = {
  Icon: FileImage,
  tile: 'bg-[var(--surface-sunken)] dark:bg-white/5',
  glyph: 'text-muted',
};

const FORMAT_STYLES: Record<string, RowStyle> = {
  web: {
    Icon: Globe,
    tile: 'bg-sky-50 dark:bg-sky-500/15',
    glyph: 'text-sky-600 dark:text-sky-400',
  },
  print: {
    Icon: Printer,
    tile: 'bg-violet-50 dark:bg-violet-500/15',
    glyph: 'text-violet-600 dark:text-violet-400',
  },
  office: {
    Icon: Presentation,
    tile: 'bg-amber-50 dark:bg-amber-500/15',
    glyph: 'text-amber-600 dark:text-amber-400',
  },
  cutting: {
    Icon: Scissors,
    tile: 'bg-emerald-50 dark:bg-emerald-500/15',
    glyph: 'text-emerald-600 dark:text-emerald-400',
  },
};

export function DownloadPanel({ assetId, fileName }: { assetId: Id; fileName: string }) {
  const { data } = useDownloadFormats(assetId);
  const [showCustom, setShowCustom] = useState(false);

  const derivable = data?.derivable ?? false;
  const cutting = data?.cutting ?? false;

  return (
    <div className="rounded-[var(--radius-card)] surface-card border line p-4">
      <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
        <Download className="size-3.5 text-brand-500" /> {t('downloadPanel.download')}
      </h2>

      {/* 2x2 rather than a four-row stack: the four options are siblings of
          equal weight, and stacked they pushed the AI tags off the fold. Falls
          back to one column on a narrow rail, where two would leave each tile
          too tight for its description. */}
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        <DownloadRow
          href={downloadUrls.original(assetId)}
          fileName={fileName}
          label={t('downloadPanel.originalFile')}
          description={t('downloadPanel.originalDescription')}
          style={ORIGINAL_STYLE}
        />
        {derivable &&
          data?.formats.map((f) => (
            <DownloadRow
              key={f.id}
              href={downloadUrls.format(assetId, f.id)}
              {...formatLabel(f)}
              style={FORMAT_STYLES[f.id] ?? ORIGINAL_STYLE}
            />
          ))}
        {cutting && <CuttingRow assetId={assetId} clippingPath={data?.clippingPath ?? false} />}
      </div>

      {derivable && (
        <>
          <button
            type="button"
            onClick={() => setShowCustom((v) => !v)}
            className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-brand-500 hover:underline"
          >
            <Sliders className="size-3.5" />
            {showCustom
              ? t('downloadPanel.hideCustomDownload')
              : t('downloadPanel.customDownload')}
          </button>
          {showCustom && <CustomDownload assetId={assetId} />}
        </>
      )}
    </div>
  );
}

/**
 * One download choice. An `<a download>` rather than a fetch: the browser
 * streams straight to disk with its own progress UI, and the session cookie
 * authenticates it same-origin.
 *
 * `download` is left off the derived formats deliberately — the server sets the
 * filename via Content-Disposition, and a bare `download` attribute here would
 * override it with the original's name and extension (a .jpg saved as .png).
 */
function DownloadRow({
  href,
  label,
  description,
  fileName,
  style,
}: {
  href: string;
  label: string;
  description: string;
  fileName?: string;
  style: RowStyle;
}) {
  const { Icon, tile, glyph } = style;
  return (
    <a
      href={href}
      {...(fileName ? { download: fileName } : {})}
      // items-start so the icon stays level with the label once the description
      // wraps, and h-full so both tiles in a row match height regardless of
      // whether their description took one line or two.
      className="group flex h-full items-start gap-2.5 rounded-md border border-line px-2.5 py-2 transition-colors hover:border-brand-500 hover:bg-[var(--surface-sunken)]"
      title={t('downloadPanel.rowTitle', { label, description })}
    >
      {/* The colour cue. aria-hidden: the label beside it already names the
          format, so announcing the icon would only repeat it. */}
      <span
        aria-hidden
        className={`flex size-7 shrink-0 items-center justify-center rounded-md ${tile}`}
      >
        <Icon className={`size-3.5 ${glyph}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-body">{label}</span>
        {/* Wraps to two lines instead of truncating: at half width "JPEG, max
            1920px — websites, email, social" would be cut to near-uselessness,
            and the whole point of the row is knowing which format to pick. */}
        <span className="block text-[10px] leading-snug text-faint">{description}</span>
      </span>
      <Download className="mt-0.5 size-3.5 shrink-0 text-faint transition-colors group-hover:text-brand-500" />
    </a>
  );
}

/**
 * The Cutting row — the die-cut mockup export.
 *
 * "Cutting" is opaque next to Web/Print/Office, and the old Pimcore bundle knew
 * it: its menu carried an "About" dialog spelling out all six properties of the
 * output. That explainer is reproduced here rather than dropped, because the
 * people who use this format care about the exact numbers — a print buyer
 * asking for "1000px, 100KB, path intact" needs to confirm this is that thing.
 *
 * The description is conditional on purpose. Only ~8% of the library carries a
 * clipping path, so a row that always read "keeps the clipping path" would be
 * wrong most of the time and would quietly erode trust in the one claim that
 * matters. When there is no path we say so and describe what it does instead.
 *
 * Orientation stays behind a disclosure because `auto` is right almost always —
 * it caps whichever axis is genuinely longer.
 */
function CuttingRow({ assetId, clippingPath }: { assetId: Id; clippingPath: boolean }) {
  const [open, setOpen] = useState(false);
  const [about, setAbout] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <DownloadRow
        href={downloadUrls.cutting(assetId)}
        label={t('downloadPanel.cutting')}
        description={
          clippingPath
            ? t('downloadPanel.cuttingWithPath')
            : t('downloadPanel.cuttingNoPath')
        }
        style={FORMAT_STYLES.cutting!}
      />
      <div className="mt-1 flex flex-wrap items-center gap-x-2 px-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[10px] text-faint hover:text-brand-500 hover:underline"
          aria-expanded={open}
        >
          {open ? t('downloadPanel.hideOrientation') : t('downloadPanel.chooseOrientation')}
        </button>
        <button
          type="button"
          onClick={() => setAbout((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] text-faint hover:text-brand-500 hover:underline"
          aria-expanded={about}
        >
          <Info className="size-2.5" aria-hidden />
          {about ? t('downloadPanel.hideDetails') : t('downloadPanel.whatsThis')}
        </button>
      </div>
      {about && (
        <div className="mt-1.5 rounded-md border border-line bg-[var(--surface-sunken)] px-2.5 py-2 text-[10px] leading-relaxed text-muted">
          <p className="mb-1.5">
            {t('downloadPanel.aboutIntro')}{' '}
            <em>{t('downloadPanel.aboutButton')}</em> {t('downloadPanel.aboutButtonSuffix')}
          </p>
          <ul className="ml-3 list-disc space-y-0.5">
            <li>{t('downloadPanel.aboutResize')}</li>
            <li>{t('downloadPanel.aboutQuality')}</li>
            <li>{t('downloadPanel.aboutMeta')}</li>
            <li>
              {clippingPath
                ? t('downloadPanel.aboutPathPreserved')
                : t('downloadPanel.aboutPathNone')}
            </li>
          </ul>
        </div>
      )}
      {open && (
        <div className="mt-1 flex flex-wrap gap-1 px-2.5">
          {(['auto', 'landscape', 'portrait'] as CuttingOrientation[]).map((o) => (
            <a
              key={o}
              href={downloadUrls.cutting(assetId, o)}
              className="rounded border border-line px-1.5 py-0.5 text-[10px] capitalize text-muted transition-colors hover:border-brand-500 hover:text-brand-500"
              title={
                o === 'auto'
                  ? t('downloadPanel.titleAuto')
                  : t('downloadPanel.capSideTitle', {
                      side: o === 'landscape' ? t('downloadPanel.width') : t('downloadPanel.height'),
                    })
              }
            >
              {o}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

const MODE_LABEL: Record<CustomMode, () => string> = {
  width: () => t('downloadPanel.scaleByWidth'),
  height: () => t('downloadPanel.scaleByHeight'),
  fit: () => t('downloadPanel.fitInBox'),
  original: () => t('downloadPanel.originalSize'),
};

const EMPTY = {
  type: 'jpeg' as CustomType,
  mode: 'width' as CustomMode,
  width: '',
  height: '',
  quality: '',
  dpi: '',
};

function CustomDownload({ assetId }: { assetId: Id }) {
  const { data } = useDownloadFormats(assetId);
  const { data: presets } = useDownloadPresets();
  const savePreset = useSavePreset();
  const deletePreset = useDeletePreset();

  const [form, setForm] = useState(EMPTY);
  const [presetId, setPresetId] = useState<number | ''>('');
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  // Editing any field means the result no longer matches the chosen preset, so
  // the selection is cleared rather than left pointing at different settings.
  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setPresetId('');
  };

  function applyPreset(p: DownloadPreset) {
    setForm({
      type: p.type,
      mode: p.mode,
      width: p.width?.toString() ?? '',
      height: p.height?.toString() ?? '',
      quality: p.quality?.toString() ?? '',
      dpi: p.dpi?.toString() ?? '',
    });
    setPresetId(p.id);
  }

  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  const needsWidth = form.mode === 'width' || form.mode === 'fit';
  const needsHeight = form.mode === 'height' || form.mode === 'fit';

  // 'fit' accepts either bound, so it's satisfied by whichever one is filled.
  const valid =
    form.mode === 'original' ||
    (form.mode === 'fit'
      ? Boolean(num(form.width) ?? num(form.height))
      : form.mode === 'width'
        ? Boolean(num(form.width))
        : Boolean(num(form.height)));

  const href = downloadUrls.custom(assetId, {
    type: form.type,
    mode: form.mode,
    width: num(form.width) ?? undefined,
    height: num(form.height) ?? undefined,
    quality: num(form.quality) ?? undefined,
    dpi: num(form.dpi) ?? undefined,
  });

  const selected = presets?.find((p) => p.id === presetId);

  return (
    <div className="mt-3 space-y-3 rounded-md border border-line p-3">
      {/* Saved presets */}
      <div className="space-y-1">
        <label className="block text-[11px] text-faint" htmlFor="preset">
          {t('downloadPanel.savedPreset')}
        </label>
        <div className="flex gap-1.5">
          <select
            id="preset"
            className={FIELD}
            value={presetId}
            onChange={(e) => {
              const id = e.target.value === '' ? '' : Number(e.target.value);
              setPresetId(id);
              const p = presets?.find((x) => x.id === id);
              if (p) applyPreset(p);
            }}
          >
            <option value="">{t('downloadPanel.customSettings')}</option>
            {presets?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.shared ? t('downloadPanel.shared') : ''}
              </option>
            ))}
          </select>
          {selected?.editable && (
            <button
              type="button"
              title={t('downloadPanel.deletePresetTitle', { name: selected.name })}
              aria-label={t('downloadPanel.deletePresetAria', { name: selected.name })}
              className="shrink-0 rounded-md border border-line px-2 text-faint transition-colors hover:border-red-500 hover:text-red-600"
              onClick={() => {
                deletePreset.mutate(selected.id, { onSuccess: () => setPresetId('') });
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Labelled label={t('downloadPanel.format')}>
          <select className={FIELD} value={form.type} onChange={(e) => set('type', e.target.value as CustomType)}>
            {data?.customTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label={t('downloadPanel.mode')}>
          <select className={FIELD} value={form.mode} onChange={(e) => set('mode', e.target.value as CustomMode)}>
            {(Object.keys(MODE_LABEL) as CustomMode[]).map((m) => (
              <option key={m} value={m}>
                {MODE_LABEL[m]()}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label={t('downloadPanel.widthLabel')}>
          <input
            className={FIELD}
            type="number"
            min={1}
            placeholder={needsWidth ? 'px' : '—'}
            disabled={!needsWidth}
            value={form.width}
            onChange={(e) => set('width', e.target.value)}
          />
        </Labelled>
        <Labelled label={t('downloadPanel.heightLabel')}>
          <input
            className={FIELD}
            type="number"
            min={1}
            placeholder={needsHeight ? 'px' : '—'}
            disabled={!needsHeight}
            value={form.height}
            onChange={(e) => set('height', e.target.value)}
          />
        </Labelled>
        <Labelled label={t('downloadPanel.quality')}>
          <input
            className={FIELD}
            type="number"
            min={1}
            max={100}
            placeholder={t('downloadPanel.source')}
            value={form.quality}
            onChange={(e) => set('quality', e.target.value)}
          />
        </Labelled>
        <Labelled label={t('downloadPanel.dpi')}>
          <input
            className={FIELD}
            type="number"
            min={1}
            placeholder={t('downloadPanel.source')}
            value={form.dpi}
            onChange={(e) => set('dpi', e.target.value)}
          />
        </Labelled>
      </div>

      <p className="text-[10px] text-faint">
        {t('downloadPanel.neverEnlarged')}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {/* An anchor, not a Button, so the browser handles the download; when the
            settings are incomplete it degrades to a disabled-looking span. */}
        {valid ? (
          <a
            href={href}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-500 px-3 text-xs font-medium text-white transition-opacity hover:opacity-90"
          >
            <Download className="size-3.5" /> {t('downloadPanel.download')}
          </a>
        ) : (
          <span
            className="inline-flex h-8 cursor-not-allowed items-center gap-1.5 rounded-md border border-line px-3 text-xs text-faint"
            title={t('downloadPanel.enterDimensionFirst', {
              dimension: form.mode === 'height' ? t('downloadPanel.height') : t('downloadPanel.width'),
            })}
          >
            <Download className="size-3.5" /> {t('downloadPanel.download')}
          </span>
        )}
        {!naming && (
          <Button variant="secondary" className="h-8 text-xs" onClick={() => setNaming(true)}>
            <Plus className="size-3.5" /> {t('downloadPanel.saveAsPreset')}
          </Button>
        )}
      </div>

      {naming && (
        <SavePresetForm
          busy={savePreset.isPending}
          error={savePreset.error as Error | null}
          name={name}
          onName={setName}
          onCancel={() => {
            setNaming(false);
            setName('');
            savePreset.reset();
          }}
          onSave={() => {
            savePreset.mutate(
              {
                name: name.trim(),
                type: form.type,
                mode: form.mode,
                width: num(form.width),
                height: num(form.height),
                quality: num(form.quality),
                dpi: num(form.dpi),
              },
              {
                onSuccess: (r) => {
                  setNaming(false);
                  setName('');
                  setPresetId(r.preset.id);
                },
              },
            );
          }}
        />
      )}
    </div>
  );
}

function SavePresetForm({
  name,
  onName,
  onSave,
  onCancel,
  busy,
  error,
}: {
  name: string;
  onName: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
  error: Error | null;
}) {
  // Focus on open so the name can be typed without reaching for the mouse.
  const [el, setEl] = useState<HTMLInputElement | null>(null);
  useEffect(() => el?.focus(), [el]);

  return (
    <form
      className="space-y-2 rounded-md border border-line p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSave();
      }}
    >
      <label className="block text-[11px] text-faint" htmlFor="preset-name">
        {t('downloadPanel.presetName')}
      </label>
      <input
        id="preset-name"
        ref={setEl}
        className={FIELD}
        value={name}
        placeholder={t('downloadPanel.presetNamePlaceholder')}
        maxLength={80}
        onChange={(e) => onName(e.target.value)}
      />
      {error && <p className="text-xs text-red-600">{error.message}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" className="h-7 text-xs" loading={busy} disabled={!name.trim()}>
          {t('downloadPanel.save')}
        </Button>
        <Button type="button" variant="secondary" className="h-7 text-xs" onClick={onCancel}>
          {t('downloadPanel.cancel')}
        </Button>
      </div>
    </form>
  );
}

const FIELD =
  'w-full rounded-md border border-line bg-[var(--surface-card)] px-2 py-1.5 text-sm text-body outline-none focus:border-brand-500 disabled:opacity-50';

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] text-faint">{label}</span>
      {children}
    </label>
  );
}
